import React, { useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { all_routes } from "../../../router/all_routes";
import DualListBox from "../../../duallistBox";
import Cookies from "js-cookie";
import { useUserPermissions } from "../../../UserPermissionsContext";
import CommonSelect, { Option } from "../../../../core/common/commonSelect";

type Permission = { id: number; name: string };

type Admin = {
  student_id: number;
  gender_name: string;
  admission_status_name: string;
  state_name: string;
  zone_name: string;
  branch_name: string;
  orientation_name: string;
  student_class_name: string;
  section_name: string;
  agent_name: string;
  academic_year_name: string;
  varna_student_id: string | null;
  SCS_Number: string;
  is_active: boolean;
  academic_year: number;
  gender: number;
  admission_status: number;
  state: number;
  zone: number;
  branch: number;
  orientation: number;
  student_class: number;
  section: number;
  agent: number;
  name: string;
};

type Disposition = {
  student_disposition_id: number;
  disposition_name: string;
};

type TicketType = {
  ticket_type_id: number;
  ticket_type: string;
};

type Category = {
  student_category_id: number;
  category_name: string;
};

type SubCategory = {
  student_subcategory_id: number;
  subcategory_name: string;
};

type Priority = {
  student_priority_id: number;
  priority_name: string;
};

interface Ticket {
  disposition?: number;
  ticket_type?: number;
  category?: number;
  subcategory?: number;
  priority?: number;
  description?: string;
}

const WizklubView = () => {
  const routes = all_routes;
  const location = useLocation();
  const { id } = location.state || {};
  const navigate = useNavigate();
  const [chosenPermissions, setChosenPermissions] = useState<Permission[]>([]);
  const [permissions, setPermissions] = useState<Permission[]>([]);
  const jwtToken = Cookies.get("authToken");
  const { userPermissions, webApi } = useUserPermissions();
  const [isLoading, setIsLoading] = useState(false);
  const [data, setData] = useState({});
  const [ticketData, setTicketData] = useState({});
  const [dispositionData, setDispositionData] = useState<Disposition[]>([]);
  const [categoriesData, setCategoriesData] = useState<Category[]>([]);
  const [subCategoryData, setSubCategoryData] = useState<SubCategory[]>([]);
  const [priorityData, setPriorityData] = useState<Priority[]>([]);
  const [typeOfTicketData, setTypeOfTicketData] = useState<TicketType[]>([]);
  const [selectedDisposition, setSelectedDisposition] =
    useState<Disposition | null>(null);
  const [selectedTypeOfTicket, setSelectedTypeOfTicket] =
    useState<TicketType | null>(null);
  const [selectedCategory, setSelectedCategory] = useState<Category | null>(
    null
  );
  const [selectedSubCategory, setSelectedSubCategory] =
    useState<SubCategory | null>(null);
  const [selectedPriority, setSelectedPriority] = useState<Priority | null>(
    null
  );
  const checkPermission = (action: any, subject: any) => {
    return userPermissions.some(
      (ability) => ability.action === action && ability.subject === subject
    );
  };

  const [newAdmin, setNewAdmin] = useState<Admin>({
    student_id: 0,
    gender_name: "",
    admission_status_name: "",
    state_name: "",
    zone_name: "",
    branch_name: "",
    orientation_name: "",
    student_class_name: "",
    section_name: "",
    agent_name: "",
    academic_year_name: "",
    varna_student_id: null,
    SCS_Number: "",
    is_active: false,
    academic_year: 0,
    gender: 0,
    admission_status: 0,
    state: 0,
    zone: 0,
    branch: 0,
    orientation: 0,
    student_class: 0,
    section: 0,
    agent: 0,
    name: "",
  });

  const [newTicket, setNewTicket] = useState<Ticket>({
    disposition: undefined,
    ticket_type: undefined,
    category: undefined,
    subcategory: undefined,
    priority: undefined,
    description: "",
  });

  const options = {
    method: "GET",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${jwtToken}`,
    },
  };

  const fetchProviderData = async (id: string) => {
    const response = await fetch(
      `${webApi}/students/students_student/${id}`,
      options
    );
    return await response.json();
  };

  const fetchTicketData = async (id: string) => {
    const response = await fetch(
      `${webApi}/wizklub/wizklub_tickets/${id}`,
      options
    );
    return await response.json();
  };

  const fetchDispositionData = async () => {
    const response = await fetch(
      `${webApi}/wizklub/dispositions_dropdown/`,
      options
    );
    return await response.json();
  };

  const fetchCategoriesData = async (
    dispositionId: number,
    ticketTypeId: number
  ) => {
    const response = await fetch(
      `${webApi}/wizklub/categories_by_disposition_and_ticket_type/${dispositionId}/${ticketTypeId}/`,
      options
    );
    return await response.json();
  };

  const fetchSubCategoryData = async (categoryId: number) => {
    const response = await fetch(
      `${webApi}/wizklub/subcategories_by_category/${categoryId}/`,
      options
    );
    return await response.json();
  };

  const fetchPriorityData = async () => {
    const response = await fetch(
      `${webApi}/wizklub/priorities_dropdown/`,
      options
    );
    return await response.json();
  };

  const fetchTypeOfTicketData = async () => {
    const response = await fetch(
      `${webApi}/wizklub/type_of_tickets_dropdown/`,
      options
    );
    return await response.json();
  };

  const fetchPermissionsData = async () => {
    const response = await fetch(
      `${webApi}/user/get_permissions_dropdown/`,
      options
    );
    return await response.json();
  };

  const fetchData = async () => {
    setIsLoading(true);
    try {
      if (id) {
        const [
          providerData,
          ticketDataResponse,
          dispositionDataResponse,
          priorityDataResponse,
          typeOfTicketDataResponse,
        ] = await Promise.all([
          fetchProviderData(id),
          fetchTicketData(id),
          fetchDispositionData(),
          fetchPriorityData(),
          fetchTypeOfTicketData(),
        ]);

        setData(providerData);
        setData(providerData);
        setTicketData(ticketDataResponse);
        setDispositionData(dispositionDataResponse);
        setPriorityData(priorityDataResponse);
        setTypeOfTicketData(typeOfTicketDataResponse);
        setNewAdmin({
          ...newAdmin,
          student_id: providerData.student_id,
          gender_name: providerData.gender_name,
          admission_status_name: providerData.admission_status_name,
          state_name: providerData.state_name,
          zone_name: providerData.zone_name,
          branch_name: providerData.branch_name,
          orientation_name: providerData.orientation_name,
          student_class_name: providerData.student_class_name,
          section_name: providerData.section_name,
          agent_name: providerData.agent_name,
          academic_year_name: providerData.academic_year_name,
          varna_student_id: providerData.varna_student_id,
          SCS_Number: providerData.SCS_Number,
          is_active: providerData.is_active,
          academic_year: providerData.academic_year,
          gender: providerData.gender,
          admission_status: providerData.admission_status,
          state: providerData.state,
          zone: providerData.zone,
          branch: providerData.branch,
          orientation: providerData.orientation,
          student_class: providerData.student_class,
          section: providerData.section,
          agent: providerData.agent,
          name: providerData.name,
        });
      } else {
        console.error("ID is undefined");
      }
    } catch (error) {
      console.error("Error fetching data:", error);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (!checkPermission("change", "Can change ticket type")) {
      Cookies.remove("authToken");
      navigate("/login");
    } else {
      fetchData();
    }
  }, [userPermissions]);

  const updateData = async () => {
    const requestOptions = {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${jwtToken}`,
      },
      body: JSON.stringify(newTicket),
    };
    const url = `${webApi}wizklub/ticket_operations/${id}/`;
    const response = await fetch(url, requestOptions);
    if (!response.ok) {
      const errorData = await response.json();

      throw new Error("Network response was not ok");
    }
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    try {
      await updateData();
      navigate(routes.studentData);
    } catch (error) {}
  };

  const handleInputChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const { name, value } = e.target;
    setNewTicket({ ...newTicket, [name]: value || null });
  };

  const dispositionOptions = dispositionData.map((d) => ({
    value: d.student_disposition_id.toString(),
    label: d.disposition_name,
  }));

  const ticketTypeOptions = typeOfTicketData.map((t) => ({
    value: t.ticket_type_id.toString(),
    label: t.ticket_type,
  }));

  const categoryOptions = categoriesData.map((c) => ({
    value: c.student_category_id.toString(),
    label: c.category_name,
  }));

  const subCategoryOptions = subCategoryData.map((sc) => ({
    value: sc.student_subcategory_id.toString(),
    label: sc.subcategory_name,
  }));

  const priorityOptions = priorityData.map((p) => ({
    value: p.student_priority_id.toString(),
    label: p.priority_name,
  }));

  const handleTicketTypeChange = async (selected: Option | null) => {
    const ticketType = typeOfTicketData.find(
      (t) => t.ticket_type_id === parseInt(selected?.value || "")
    );
    setSelectedTypeOfTicket(ticketType || null);
    setNewTicket({
      ...newTicket,
      ticket_type: ticketType?.ticket_type_id,
    });

    if (ticketType && selectedDisposition) {
      const categoriesDataResponse = await fetchCategoriesData(
        selectedDisposition.student_disposition_id,
        ticketType.ticket_type_id
      );
      setCategoriesData(categoriesDataResponse);
    }
  };

  const handleDispositionChange = async (selected: Option | null) => {
    const disposition = dispositionData.find(
      (d) => d.student_disposition_id === parseInt(selected?.value || "")
    );
    setSelectedDisposition(disposition || null);
    setNewTicket({
      ...newTicket,
      disposition: disposition?.student_disposition_id,
    });

    if (disposition && selectedTypeOfTicket) {
      const categoriesDataResponse = await fetchCategoriesData(
        disposition.student_disposition_id,
        selectedTypeOfTicket.ticket_type_id
      );
      setCategoriesData(categoriesDataResponse.results);
    }
  };

  const handleCategoryChange = async (selected: Option | null) => {
    const category = categoriesData.find(
      (c) => c.student_category_id === parseInt(selected?.value || "")
    );
    setSelectedCategory(category || null);
    setNewTicket({
      ...newTicket,
      category: category?.student_category_id,
    });

    if (category) {
      const subCategoryDataResponse = await fetchSubCategoryData(
        category.student_category_id
      );
      setSubCategoryData(subCategoryDataResponse.results);
    }
  };

  const handleSubCategoryChange = (selected: Option | null) => {
    const subCategory = subCategoryData.find(
      (sc) => sc.student_subcategory_id === parseInt(selected?.value || "")
    );
    setSelectedSubCategory(subCategory || null);
    setNewTicket({
      ...newTicket,
      subcategory: subCategory?.student_subcategory_id,
    });
  };

  const handlePriorityChange = (selected: Option | null) => {
    const priority = priorityData.find(
      (p) => p.student_priority_id === parseInt(selected?.value || "")
    );
    setSelectedPriority(priority || null);
    setNewTicket({
      ...newTicket,
      priority: priority?.student_priority_id,
    });
  };

  useEffect(() => {
    // Fetch all the forms we want to apply custom Bootstrap validation styles to
    const forms = document.querySelectorAll(
      ".needs-validation"
    ) as NodeListOf<HTMLFormElement>;

    // Loop over them and prevent submission
    Array.from(forms).forEach((form) => {
      form.addEventListener(
        "submit",
        (event) => {
          if (!form.checkValidity()) {
            event.preventDefault();
            event.stopPropagation();
          }

          form.classList.add("was-validated");
        },
        false
      );
    });
  }, []);
  return (
    <div>
      <div className="page-wrapper">
        <div className="content container-fluid">
          {/* Page Header */}
          <div className="page-header">
            <div className="row">
              <div className="col-sm-12">
                <h3 className="page-title">Wizklub View</h3>
                <ul className="breadcrumb">
                  <li className="breadcrumb-item">
                    <Link to={routes.adminDashboard}>Dashboard</Link>
                  </li>
                  <li className="breadcrumb-item">
                    <Link to={routes.studentData}>Add Ticket</Link>
                  </li>
                  <li className="breadcrumb-item active">Add Ticket</li>
                </ul>
              </div>
            </div>
          </div>
          {/* /Page Header */}

          {/* Non-editable Fields Card */}
          <div
            className="card"
            style={{
              width: "25%",
              margin: "10px",
              float: "left",
              boxShadow: "0 4px 12px rgba(0, 0, 0, 0.1)",
              borderRadius: "12px",

              backgroundColor: "#f3f4f6",
              border: "1px solid #d1d5db",
            }}
          >
            <div className="card-body">
              <h5
                className="card-title"
                style={{ fontWeight: "bold", marginBottom: "15px" }}
              >
                Details
              </h5>
              <div
                className="detail-row"
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  padding: "10px 0",
                  borderBottom: "1px solid #e5e7eb",
                }}
              >
                <span
                  className="detail-key"
                  style={{ fontWeight: "bold", color: "#4b5563" }}
                >
                  SCS Number:
                </span>
                <span
                  className="detail-value"
                  style={{ textAlign: "right", color: "#1f2937" }}
                >
                  {newAdmin.SCS_Number}
                </span>
              </div>
              <div
                className="detail-row"
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  padding: "10px 0",
                  borderBottom: "1px solid #e5e7eb",
                }}
              >
                <span
                  className="detail-key"
                  style={{ fontWeight: "bold", color: "#4b5563" }}
                >
                  Student:
                </span>
                <span
                  className="detail-value"
                  style={{ textAlign: "right", color: "#1f2937" }}
                >
                  {newAdmin.name}
                </span>
              </div>
              <div
                className="detail-row"
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  padding: "10px 0",
                  borderBottom: "1px solid #e5e7eb",
                }}
              >
                <span
                  className="detail-key"
                  style={{ fontWeight: "bold", color: "#4b5563" }}
                >
                  Gender:
                </span>
                <span
                  className="detail-value"
                  style={{ textAlign: "right", color: "#1f2937" }}
                >
                  {newAdmin.gender_name}
                </span>
              </div>
              <div
                className="detail-row"
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  padding: "10px 0",
                  borderBottom: "1px solid #e5e7eb",
                }}
              >
                <span
                  className="detail-key"
                  style={{ fontWeight: "bold", color: "#4b5563" }}
                >
                  Admission Status :
                </span>
                <span
                  className="detail-value"
                  style={{ textAlign: "right", color: "#1f2937" }}
                >
                  {newAdmin.admission_status_name}
                </span>
              </div>
            </div>
          </div>

          {/* Editable Form */}
          <div className="row justify-content-center">
            <div className="col-sm-12 d-flex justify-content-center">
              <div
                className="card"
                style={{
                  width: "100%",
                  margin: "10px",
                  boxShadow: "0 4px 12px rgba(0, 0, 0, 0.1)",
                  borderRadius: "12px",
                  padding: "20px",
                  backgroundColor: "#fff",
                  border: "1px solid #d1d5db",
                }}
              >
                <div className="card-body">
                  <form onSubmit={handleSubmit}>
                    <div
                      className="modal-body light-violet-bg d-flex"
                      style={{ flexWrap: "wrap" }}
                    >
                      <div
                        className="mb-3"
                        style={{
                          flex: "1 1 100%",
                          maxWidth: "300px",
                          marginRight: "15px",
                        }}
                      >
                        <label className="form-label">Ticket Type</label>
                        <CommonSelect
                          className="select"
                          options={ticketTypeOptions}
                          onChange={handleTicketTypeChange}
                        />
                      </div>
                      <div
                        className="mb-3 me-3"
                        style={{
                          flex: "1 1 100%",
                          maxWidth: "300px",
                          marginRight: "15px",
                        }}
                      >
                        <label className="form-label">Disposition</label>
                        <CommonSelect
                          className="select"
                          options={dispositionOptions}
                          onChange={handleDispositionChange}
                        />
                      </div>
                      <div
                        className="mb-3 me-3"
                        style={{
                          flex: "1 1 100%",
                          maxWidth: "300px",
                          marginRight: "15px",
                        }}
                      >
                        <label className="form-label">Category</label>
                        <CommonSelect
                          className="select"
                          options={categoryOptions}
                          onChange={handleCategoryChange}
                        />
                      </div>
                      <div
                        className="mb-3"
                        style={{
                          flex: "1 1 100%",
                          maxWidth: "350px",
                          marginRight: "15px",
                        }}
                      >
                        <label className="form-label">Sub-Category</label>
                        <CommonSelect
                          className="select"
                          options={subCategoryOptions}
                          onChange={handleSubCategoryChange}
                        />
                      </div>
                      <div
                        className="mb-3 me-3"
                        style={{
                          flex: "1 1 100%",
                          maxWidth: "350px",
                          marginRight: "15px",
                        }}
                      >
                        <label className="form-label">Priority</label>
                        <CommonSelect
                          className="select"
                          options={priorityOptions}
                          onChange={handlePriorityChange}
                        />
                      </div>
                      <div
                        className="form-group mb-3"
                        style={{ flex: "1 1 100%", minWidth: "200px" }}
                      >
                        <label className="form-label">Description</label>
                        <textarea
                          className="form-control"
                          name="description"
                          placeholder="Description"
                          onChange={handleInputChange}
                          value={newTicket.description || ""}
                          rows={4} // Make sure to use number directly
                        />
                      </div>
                    </div>
                    <div className="modal-footer">
                      <Link
                        className="btn btn-primary"
                        to={routes.studentData}
                        style={{
                          backgroundColor: "lightcoral",
                          color: "white",
                          marginRight: "5px",
                        }}
                      >
                        Cancel
                      </Link>
                      <button className="btn btn-primary" type="submit">
                        Save
                      </button>
                    </div>
                  </form>
                </div>
              </div>
              {/* /Add Group */}
            </div>
          </div>
          {/* /Row */}
        </div>
      </div>
    </div>
  );

  function formatKey(key: string) {
    return key
      .replace(/_/g, " ")
      .replace(/\b\w/g, (char: string) => char.toUpperCase());
  }
};

export default WizklubView;
